import Button from "components/Button";
import Loading from "components/Loading";
import Dashboard from "components/Template/Dashboard";
import handleErrors from "helpers/handleErrors";
import IconArrowRight from "icons/arrow-circle-right-white.svg";
import React, { useEffect, useState } from "react";
import ArrowLeft from "icons/arrow-circle-left-green.svg";
import { useHistory } from "react-router-dom";
import { schemaChangePayments } from "./validation";
import TextInputAuth from "components/TextInputAuth";
import SelectInputAuth from "components/SelectInputAuth";
import PhoneInputAuth from "components/PhoneInputAuth";
import { statesUS } from "helpers/handleStates";
import { UpdateGroupSetBillingInformation } from "services/manageFamilies";
import toastMessage from "helpers/handleToastMessage";
import { ToastContainer } from "react-toastify";
import {
  EElementType,
  StripeInputElement,
} from "components/StripeInputElement";
import { useElements, useStripe } from "@stripe/react-stripe-js";

type Props = {};

const UpdateBilling: React.FC<Props> = (): JSX.Element => {
  let history = useHistory();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<{ id: string; name: string }[]>();

  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [groupId, setGroupId] = useState("");

  const [errors, setErrors] = useState<any>();

  useEffect(() => {
    setGroupId(history.location.pathname.split("/")[2]);
    const processedStates = statesUS.map((state: any) => {
      return { id: state.abbreviation, name: state.name };
    });

    setStates(processedStates);
  }, [history]);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }
    try {
      setLoading(true);

      const cardElement = elements.getElement("cardNumber");

      if (!cardElement) {
        toastMessage({
          message: "Error, Something went wrong.",
          type: "error",
        });
        return;
      }

      const result = await stripe.createToken(cardElement, {
        name: "",
      });

      if (!result.token) {
        toastMessage({
          message: "Error, Something went wrong.",
          type: "error",
        });
        return;
      }

      const stateData = {
        fullName,
        address,
        city,
        zip,
        state,
        phone,
        email,
      };

      if (!schemaChangePayments.isValidSync(stateData)) {
        handleErrors({
          schema: schemaChangePayments,
          stateData: stateData,
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      await UpdateGroupSetBillingInformation(groupId, {
        address,
        city,
        state,
        zip,
        country: "US",
        email,
        phoneNumber: phone,
        cardName: fullName,
        creditCardTokenId: result.token.id,
        cardBrand: result.token.card?.brand,
        cardExpiryMonth: result.token.card?.exp_month,
        cardExpiryYear: result.token.card?.exp_year,
        cardLast4: result.token.card?.last4,
      });

      toastMessage({
        message: "Billing update!",
        type: "success",
      });

      history.push(`/manage-families/${groupId}/details`);
    } catch (error: any) {
      toastMessage({
        message: "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dashboard
      icon={ArrowLeft}
      breadcrumb={[
        { name: "Manage Families", active: false, path: "manage-families" },
        {
          name: history.location.state.groupName,
          active: false,
          path: `/manage-families/${groupId}/details`,
        },
        { name: "Edit", active: true },
      ]}
      title={"Payment Details"}
      children={
        <div className="bg-bt-white p-4">
          <div className="w-9/12 mx-auto mt-4">
            <Loading loading={loading} />
            <div className="flex flex-row">
              <div className="flex flex-col w-1/2 mx-2">
                <TextInputAuth
                  placeholder={"Enter the name visible on your credit card"}
                  label="Cardholder Name"
                  value={fullName}
                  onChange={(e: any) => setFullName(e.target.value)}
                  isHelp={errors?.fullName}
                  helpText={errors?.fullName}
                />
                <StripeInputElement type={EElementType.Number} />
                <StripeInputElement type={EElementType.CVC} />
                <StripeInputElement type={EElementType.Expiry} />
              </div>
              <div className="flex flex-col w-1/2 mx-2">
                <TextInputAuth
                  placeholder={"Enter your address"}
                  label="Address"
                  value={address}
                  onChange={(e: any) => setAddress(e.target.value)}
                  isHelp={errors?.address}
                  helpText={errors?.address}
                />
                <TextInputAuth
                  placeholder={"Enter your city"}
                  label="City"
                  value={city}
                  onChange={(e: any) => setCity(e.target.value)}
                  isHelp={errors?.city}
                  helpText={errors?.city}
                />
                <div className="flex flex-row w-full">
                  <div className="flex flex-col w-1/2 mr-2">
                    <TextInputAuth
                      placeholder={"Enter your ZIP Code"}
                      label="ZIP Code"
                      value={zip}
                      onChange={(e: any) => setZip(e.target.value)}
                      isHelp={errors?.zip}
                      helpText={errors?.zip}
                      maxLength={5}
                    />
                  </div>
                  <div className="flex flex-col w-1/2 ml-1">
                    <SelectInputAuth
                      placeholder={"Enter your state"}
                      label="State"
                      value={state}
                      onChange={(e: any) => setState(e.target.value)}
                      isHelp={errors?.state}
                      helpText={errors?.state}
                      options={states || []}
                    />
                  </div>
                </div>
                <PhoneInputAuth
                  placeholder={"(1) 234 3453534"}
                  label="Phone"
                  value={phone}
                  onChange={(e: any) => setPhone(e.target.value)}
                  isHelp={errors?.phone}
                  helpText={errors?.phone}
                />
                <TextInputAuth
                  placeholder={"Enter your email"}
                  label="Email"
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  isHelp={errors?.email}
                  helpText={errors?.email}
                />
              </div>
            </div>
            {/* <div className="flex flex-row">
              <div className="flex flex-col w-1/2">
                <TextInputAuth
                  placeholder={"Name as credit card"}
                  label="Full Name (as visible on the card)"
                  value={fullName}
                  onChange={(e: any) => setFullName(e.target.value)}
                  isHelp={errors?.fullName}
                  helpText={errors?.fullName}
                />
                <TextInputAuth
                  placeholder={"1234123412341234"}
                  label="Card number"
                  value={cardNumber}
                  onChange={(e: any) => setCardNumber(e.target.value)}
                  isHelp={errors?.cardNumber}
                  helpText={errors?.cardNumber}
                  maxLength={16}
                />
                <div className="flex flex-row w-full">
                  <div className="flex flex-col w-1/2">
                    <div className="flex flex-row w-full">
                      <div className="flex flex-col w-1/2">
                        <SelectInputAuth
                          placeholder={"MM"}
                          label="Month"
                          value={expirationDateMonth}
                          onChange={(e: any) =>
                            setExpirationDateMonth(e.target.value)
                          }
                          isHelp={errors?.expirationDateMonth}
                          helpText={errors?.expirationDateMonth}
                          options={[
                            { id: "1", name: "01" },
                            { id: "2", name: "02" },
                            { id: "3", name: "03" },
                            { id: "4", name: "04" },
                            { id: "5", name: "05" },
                            { id: "6", name: "06" },
                            { id: "7", name: "07" },
                            { id: "8", name: "08" },
                            { id: "9", name: "09" },
                            { id: "10", name: "10" },
                            { id: "11", name: "11" },
                            { id: "12", name: "12" },
                          ]}
                        />
                      </div>
                      <div className="flex flex-col w-1/2">
                        <TextInputAuth
                          placeholder={"AAAA"}
                          label="Year"
                          value={expirationDateYear}
                          onChange={(e: any) =>
                            setExpirationDateYear(e.target.value)
                          }
                          isHelp={errors?.expirationDateYear}
                          helpText={errors?.expirationDateYear}
                          maxLength={4}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col w-1/2">
                    <TextInputAuth
                      placeholder={"1234"}
                      label="CVV"
                      value={cvv}
                      onChange={(e: any) => setCvv(e.target.value)}
                      isHelp={errors?.cvv}
                      helpText={errors?.cvv}
                      maxLength={4}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-1/2">
                <TextInputAuth
                  placeholder={"Address"}
                  label="Address"
                  value={address}
                  onChange={(e: any) => setAddress(e.target.value)}
                  isHelp={errors?.address}
                  helpText={errors?.address}
                />
                <TextInputAuth
                  placeholder={"City"}
                  label="City"
                  value={city}
                  onChange={(e: any) => setCity(e.target.value)}
                  isHelp={errors?.city}
                  helpText={errors?.city}
                />
                <div className="flex flex-row w-full">
                  <div className="flex flex-col w-1/2">
                    <TextInputAuth
                      placeholder={"12345"}
                      label="ZIP Code"
                      value={zip}
                      onChange={(e: any) => setZip(e.target.value)}
                      isHelp={errors?.zip}
                      helpText={errors?.zip}
                      maxLength={5}
                    />
                  </div>
                  <div className="flex flex-col w-1/2">
                    <SelectInputAuth
                      placeholder={"State"}
                      label="State"
                      value={state}
                      onChange={(e: any) => setState(e.target.value)}
                      isHelp={errors?.state}
                      helpText={errors?.state}
                      options={states || []}
                    />
                  </div>
                </div>
                <PhoneInputAuth
                  placeholder={"(1) 234 3453534"}
                  label="Phone"
                  value={phone}
                  onChange={(e: any) => setPhone(e.target.value)}
                  isHelp={errors?.phone}
                  helpText={errors?.phone}
                />
                <TextInputAuth
                  placeholder={"Email"}
                  label="Email"
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  isHelp={errors?.email}
                  helpText={errors?.email}
                />
              </div>
            </div> */}
          </div>
          <div className="flex flex-row justify-end mt-5">
            <Button
              label={"Confirm"}
              className="h-14 w-32 bg-bt-green text-base text-white rounded-full font-sora font-medium2"
              iconRight={IconArrowRight}
              onClick={handleSubmit}
            />
          </div>
          <ToastContainer />
        </div>
      }
    />
  );
};

export default UpdateBilling;
